import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import Keycloak from "keycloak-js";
import { environment } from "src/environments/environment";

type CrispCommand = [string, ...unknown[]];

interface CrispWindow extends Window {
  $crisp?: CrispCommand[];
  CRISP_WEBSITE_ID?: string;
}

@Injectable({ providedIn: "root" })
export class CrispService {
  private readonly document = inject(DOCUMENT);
  private readonly keycloak = inject(Keycloak, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);
  private readonly websiteId = environment.crispWebsiteId;

  private initialized = false;

  public initialize(): void {
    if (!isPlatformBrowser(this.platformId) || !this.websiteId || this.initialized) {
      return;
    }

    const crispWindow = window as CrispWindow;
    crispWindow.$crisp ??= [];
    crispWindow.CRISP_WEBSITE_ID = this.websiteId;

    crispWindow.$crisp.push(["do", "chat:hide"]);
    crispWindow.$crisp.push([
      "on",
      "chat:closed",
      (): void => {
        crispWindow.$crisp?.push(["do", "chat:hide"]);
      },
    ]);

    if (this.document.querySelector('script[data-crisp-chat="true"]')) {
      this.initialized = true;
      void this.setLoggedInUserEmail();
      return;
    }

    const script = this.document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    script.dataset["crispChat"] = "true";

    this.document.head.append(script);
    this.initialized = true;

    void this.setLoggedInUserEmail();
  }

  public openChat(): void {
    if (!this.websiteId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialize();
    void this.setLoggedInUserEmail();

    const crispWindow = window as CrispWindow;
    crispWindow.$crisp ??= [];
    crispWindow.$crisp.push(["do", "chat:show"]);
    crispWindow.$crisp.push(["do", "chat:open"]);
  }

  public setUserEmail(email: string | null | undefined): void {
    if (!this.websiteId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const normalizedEmail = email?.trim();
    if (!normalizedEmail) {
      return;
    }

    const crispWindow = window as CrispWindow;
    crispWindow.$crisp ??= [];
    crispWindow.$crisp.push(["set", "user:email", [normalizedEmail]]);
  }

  private async setLoggedInUserEmail(): Promise<void> {
    if (!this.keycloak?.authenticated) {
      return;
    }

    const tokenEmail = this.getTokenEmail();
    if (tokenEmail) {
      this.setUserEmail(tokenEmail);
      return;
    }

    try {
      const profile = this.keycloak.profile ?? (await this.keycloak.loadUserProfile());
      this.setUserEmail(profile.email);
    } catch (error) {
      console.error("Failed to set Crisp user email", error);
    }
  }

  private getTokenEmail(): string | null {
    if (!this.keycloak) {
      return null;
    }

    const tokenParsed = this.keycloak.tokenParsed as { email?: unknown } | undefined;
    return typeof tokenParsed?.email === "string" ? tokenParsed.email : null;
  }
}
